import { useCallback, type KeyboardEvent } from 'react';
import { CornerLeftUpIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useCoarsePointer } from '@/shared/hooks/useCoarsePointer';
import { useRowDragBinding } from '../fileDragContext';

/**
 * The `..` row: a way back to the containing folder, sitting where the reader
 * is already pointing.
 *
 * It is a navigation affordance, not an entry, and it is deliberately kept out
 * of the listing's entries array (spec 059 research R4). That is what makes its
 * exclusions structural rather than a list of guards: sorting, typeahead,
 * selection and Delete all work from that array, so a row which is not in it
 * cannot be sorted, typed to, selected or deleted, and no future change to
 * those features can accidentally include it.
 *
 * The breadcrumb above the list stays: it reports *where the reader is*, while
 * this reports *somewhere they can go* — and, unlike the breadcrumb, it is a
 * drop target for moving an item up a level.
 */
export function ParentDirectoryRow({
  active,
  parentPath,
  onActivate,
  onKeyDown,
  onFocus,
}: {
  active: boolean;
  /** Where a dropped item would go — the folder containing the current one. */
  parentPath?: string;
  onActivate: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => void;
  onFocus: () => void;
}) {
  const drag = useRowDragBinding();
  // The entry rows' height, 44 on a touch screen (spec 272).
  const coarse = useCoarsePointer();
  // The cheapest way up: an item can be dropped here to leave the folder the
  // reader is standing in (FR-029), and unlike the breadcrumb it is already
  // under the pointer.
  const droppable = drag.useRowDroppable(
    parentPath === undefined ? undefined : { kind: 'parent', path: parentPath },
  );

  // Memoized for the same reason as the entry rows': a ref callback whose
  // identity changes each render makes React detach and re-attach the node, and
  // dnd-kit re-measures it every time.
  const { setNodeRef: setDroppableNode } = droppable;
  const setRowRef = useCallback(
    (element: HTMLElement | null) => {
      setDroppableNode?.(element);
    },
    [setDroppableNode],
  );

  return (
    <div
      role="row"
      aria-rowindex={2}
      aria-label="Go to parent folder"
      data-row-index={-1}
      data-testid="parent-directory-row"
      data-drop-state={
        droppable.isOver
          ? 'accepting'
          : droppable.refusing
            ? 'refusing'
            : undefined
      }
      ref={setRowRef}
      tabIndex={active ? 0 : -1}
      onFocus={onFocus}
      onKeyDown={onKeyDown}
      onClick={onActivate}
      className={cn(
        'border-border hover:bg-accent/50 focus-visible:ring-ring flex cursor-pointer items-center border-b focus-visible:ring-3 focus-visible:outline-none focus-visible:-outline-offset-2',
        droppable.isOver && 'bg-accent ring-primary ring-2 ring-inset',
        droppable.refusing && 'ring-destructive ring-2 ring-inset',
      )}
      style={{ height: coarse ? 44 : 36 }}
    >
      <div
        role="gridcell"
        aria-colindex={1}
        className="text-muted-foreground flex min-w-0 flex-1 items-center gap-2 px-3 text-left text-sm"
      >
        <CornerLeftUpIcon
          aria-hidden="true"
          className="size-4 shrink-0"
          strokeWidth={1.75}
        />
        <span>..</span>
      </div>
      {/*
        Empty by design (FR-014): this row has no kind, no size and no
        modification time, and offering it a delete action would be offering to
        delete the folder the reader is standing in.
      */}
      {/* Left out on a phone, as the listing's own kind and size (spec 173). */}
      <div
        role="gridcell"
        aria-colindex={2}
        className="w-20 shrink-0 max-sm:hidden"
      />
      <div
        role="gridcell"
        aria-colindex={3}
        className="w-16 shrink-0 max-sm:hidden"
      />
      <div role="gridcell" aria-colindex={4} className="w-20 shrink-0" />
      <div role="gridcell" aria-colindex={5} className="w-8 shrink-0" />
      <div role="gridcell" aria-colindex={6} className="w-8 shrink-0" />
      <div role="gridcell" aria-colindex={7} className="w-8 shrink-0" />
    </div>
  );
}
